import { buildImageOnlyPdfFromCanvas } from './generate-image-pdf';
import {
    FixtureOrientation,
    FixtureQuality,
    FixtureRecipe,
    FixtureTemplateId,
    jpegQualityFor,
    receiptMoneyParts
} from './fixture-recipe';
import { formatDate, formatMoneyPlain, SwProfile } from './star-wars-profile';

export interface RasterPaintResult {
    canvas: HTMLCanvasElement;
    width: number;
    height: number;
}

function ssnLast4(p: SwProfile): string {
    return (p.ssnDecrypted || '').replace(/\D/g, '').slice(-4);
}

function ssnFull(p: SwProfile): string {
    const d = (p.ssnDecrypted || '').replace(/\D/g, '');
    if (d.length < 9) {
        return d;
    }
    return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

function makeCanvas(w: number, h: number): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
}

function fillPage(ctx: CanvasRenderingContext2D, w: number, h: number) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = '#111111';
    ctx.textBaseline = 'top';
}

function drawFooter(ctx: CanvasRenderingContext2D, y: number, w: number) {
    ctx.font = '11px Courier New, monospace';
    ctx.fillStyle = '#444444';
    ctx.fillText('FICTIONAL STAR WARS SAMPLE DATA - NOT FOR FILING', 24, y);
    ctx.fillText('NKR Document Find Lab', 24, y + 16);
    void w;
}

function kv(ctx: CanvasRenderingContext2D, label: string, value: string, x: number, y: number, size: number): number {
    ctx.font = size + 'px Courier New, monospace';
    ctx.fillStyle = '#111111';
    ctx.fillText(label + ': ' + value, x, y);
    return y + size + 10;
}

function line(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, size: number, weight?: string): number {
    ctx.font = (weight || 'normal') + ' ' + size + 'px Courier New, monospace';
    ctx.fillStyle = '#111111';
    ctx.fillText(text, x, y);
    return y + size + 8;
}

function paintThermalReceipt(p: SwProfile): RasterPaintResult {
    const w = 420;
    const h = 980;
    const canvas = makeCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return { canvas, width: w, height: h };
    }
    fillPage(ctx, w, h);
    const money = receiptMoneyParts(p);
    let y = 28;
    y = line(ctx, p.merchant.toUpperCase(), 24, y, 18, 'bold');
    y = line(ctx, p.city + ', ' + p.planet, 24, y, 12);
    y = line(ctx, '--------------------------------', 24, y, 12);
    y = kv(ctx, 'Guest', p.fullName, 24, y, 13);
    y = kv(ctx, 'Alias', p.alias, 24, y, 13);
    y = line(ctx, '--------------------------------', 24, y, 12);
    y += 6;
    y = line(ctx, 'Travel supply kit', 24, y, 13);
    y = line(ctx, '  $' + formatMoneyPlain(money.line1), 24, y, 13);
    y += 8;
    y = line(ctx, 'Hyperfuel pack', 24, y, 13);
    y = line(ctx, '  $' + formatMoneyPlain(money.line2), 24, y, 13);
    y += 8;
    y = line(ctx, '1 Americano', 24, y, 13);
    y = line(ctx, '  $' + formatMoneyPlain(money.line3), 24, y, 13);
    y += 14;
    y = line(ctx, '--------------------------------', 24, y, 12);
    y += 10;
    y = line(ctx, 'SUBTOTAL  $' + formatMoneyPlain(money.subtotal), 24, y, 15, 'bold');
    y += 14;
    y = line(ctx, 'TAX (%)   $' + formatMoneyPlain(money.tax), 24, y, 15);
    y += 14;
    y = line(ctx, 'TOTAL     $' + formatMoneyPlain(money.total), 24, y, 16, 'bold');
    y += 18;
    y = line(ctx, '--------------------------------', 24, y, 12);
    y = kv(ctx, 'Date', formatDate(p.receiptDate || ''), 24, y, 13);
    y = kv(ctx, 'Route', p.route, 24, y, 12);
    y += 20;
    drawFooter(ctx, y, w);
    return { canvas, width: w, height: h };
}

function paintHotelFolio(p: SwProfile): RasterPaintResult {
    const w = 680;
    const h = 880;
    const canvas = makeCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return { canvas, width: w, height: h };
    }
    fillPage(ctx, w, h);
    let y = 36;
    y = line(ctx, p.vendor.toUpperCase(), 40, y, 22, 'bold');
    y = line(ctx, 'GUEST FOLIO', 40, y, 16);
    y = line(ctx, p.city + ', ' + p.planet, 40, y, 13);
    y += 8;
    y = line(ctx, '================================================', 40, y, 12);
    y = kv(ctx, 'Guest', p.fullName, 40, y, 14);
    y = kv(ctx, 'Alias', p.alias, 40, y, 14);
    y = kv(ctx, 'Room', 'Suite ' + (100 + (p.seed % 40)), 40, y, 14);
    y = kv(ctx, 'Ship', p.ship, 40, y, 14);
    y += 10;
    y = line(ctx, '================================================', 40, y, 12);
    y = kv(ctx, 'Lodging', formatMoneyPlain((p.receiptAmount || 0) * 0.8), 40, y, 14);
    y = kv(ctx, 'Resort fee', formatMoneyPlain((p.receiptAmount || 0) * 0.2), 40, y, 14);
    y += 16;
    y = line(ctx, 'TOTAL  $' + formatMoneyPlain(p.receiptAmount || 0), 40, y, 18, 'bold');
    y += 16;
    y = kv(ctx, 'Date', formatDate(p.receiptDate || ''), 40, y, 14);
    y += 28;
    drawFooter(ctx, y, w);
    return { canvas, width: w, height: h };
}

function paintPaystubSnippet(p: SwProfile): RasterPaintResult {
    const w = 720;
    const h = 900;
    const canvas = makeCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return { canvas, width: w, height: h };
    }
    fillPage(ctx, w, h);
    let y = 36;
    y = line(ctx, p.employer, 40, y, 18, 'bold');
    y = line(ctx, 'EMPLOYEE PAY STATEMENT', 40, y, 20, 'bold');
    y += 8;
    y = line(ctx, '================================================', 40, y, 12);
    y = kv(ctx, 'Employee', p.fullName, 40, y, 14);
    y = kv(ctx, 'First', p.firstName || '', 40, y, 14);
    y = kv(ctx, 'Last', p.lastName || '', 40, y, 14);
    y = kv(ctx, 'Alias', p.alias, 40, y, 14);
    y = kv(ctx, 'SSN', 'XXX-XX-' + ssnLast4(p), 40, y, 14);
    y += 12;
    y = line(ctx, '================================================', 40, y, 12);
    y = kv(ctx, 'Stub start', formatDate(p.incomePayStubStartDate || ''), 40, y, 14);
    y = kv(ctx, 'Stub end', formatDate(p.incomePayStubEndDate || ''), 40, y, 14);
    y += 14;
    y = line(ctx, 'STUB GROSS  $' + formatMoneyPlain(p.incomePayStubGross || 0), 40, y, 18, 'bold');
    y += 12;
    y = kv(ctx, 'Net pay', formatMoneyPlain((p.incomePayStubGross || 0) * 0.72), 40, y, 14);
    y += 28;
    drawFooter(ctx, y, w);
    return { canvas, width: w, height: h };
}

function paintTaxSnippet(p: SwProfile): RasterPaintResult {
    const w = 720;
    const h = 900;
    const canvas = makeCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return { canvas, width: w, height: h };
    }
    fillPage(ctx, w, h);
    let y = 36;
    y = line(ctx, 'Form W-2 Sample (Fictional)', 40, y, 20, 'bold');
    y = line(ctx, p.employer, 40, y, 15);
    y += 8;
    y = line(ctx, '================================================', 40, y, 12);
    y = kv(ctx, 'Employee name', p.fullName, 40, y, 14);
    y = kv(ctx, 'SSN', ssnFull(p), 40, y, 14);
    y = kv(ctx, 'DOB', formatDate(p.birthDate || ''), 40, y, 14);
    y = kv(ctx, 'Tax year', String(p.incomeIrsYear || ''), 40, y, 14);
    y += 16;
    y = line(ctx, 'IRS GROSS  $' + formatMoneyPlain(p.incomeIrsYearGross || 0), 40, y, 18, 'bold');
    y += 12;
    y = kv(ctx, 'Email', p.email || '', 40, y, 14);
    y += 28;
    drawFooter(ctx, y, w);
    return { canvas, width: w, height: h };
}

function paintLineItem(p: SwProfile): RasterPaintResult {
    const w = 480;
    const h = 720;
    const canvas = makeCanvas(w, h);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return { canvas, width: w, height: h };
    }
    fillPage(ctx, w, h);
    let y = 32;
    y = line(ctx, p.merchant.toUpperCase(), 28, y, 18, 'bold');
    y = line(ctx, 'LINE ITEM RECEIPT #01', 28, y, 14);
    y += 6;
    y = line(ctx, '--------------------------------', 28, y, 12);
    y = kv(ctx, 'Sold to', p.fullName, 28, y, 13);
    y = kv(ctx, 'Alias', p.alias, 28, y, 13);
    y = kv(ctx, 'Item', 'Travel supply kit 1', 28, y, 13);
    y += 12;
    y = line(ctx, 'AMOUNT  $' + formatMoneyPlain(p.receiptAmount || 0), 28, y, 16, 'bold');
    y += 12;
    y = kv(ctx, 'Date', formatDate(p.receiptDate || ''), 28, y, 13);
    y = kv(ctx, 'City', p.city, 28, y, 13);
    y += 24;
    drawFooter(ctx, y, w);
    return { canvas, width: w, height: h };
}

export function paintFixtureTemplate(templateId: FixtureTemplateId, profile: SwProfile): RasterPaintResult {
    if (templateId === 'hotel_folio') {
        return paintHotelFolio(profile);
    }
    if (templateId === 'paystub') {
        return paintPaystubSnippet(profile);
    }
    if (
        templateId === 'tax'
        || templateId === 'w2'
        || templateId === 'social_security'
        || templateId === 'affidavit'
    ) {
        return paintTaxSnippet(profile);
    }
    if (templateId === 'line_item') {
        return paintLineItem(profile);
    }
    return paintThermalReceipt(profile);
}

function mulberry32(a: number): () => number {
    return function () {
        let t = (a += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

export function applyDirtyEffects(
    source: HTMLCanvasElement,
    quality: FixtureQuality,
    seed: number
): HTMLCanvasElement {
    if (quality === 'clean') {
        return source;
    }
    const tier =
        quality === 'dirty-hostile' ? 4 :
        quality === 'dirty-severe' ? 3 :
        quality === 'dirty-heavy' ? 2 : 1;
    const rand = mulberry32(seed * 997 + tier * 17 + 7);
    const angleDeg = [0, 1.4, 3.2, 5.0, 7.5][tier];
    const angle = angleDeg * (rand() > 0.5 ? 1 : -1) * (Math.PI / 180);
    const pad = [0, 16, 28, 40, 56][tier];
    const out = makeCanvas(source.width + pad * 2, source.height + pad * 2);
    const ctx = out.getContext('2d');
    if (!ctx) {
        return source;
    }
    const fills = ['#ffffff', '#fafafa', '#f3f0e8', '#ebe6dc', '#e2d8c8'];
    ctx.fillStyle = fills[tier];
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.save();
    ctx.translate(out.width / 2, out.height / 2);
    ctx.rotate(angle);
    if (tier === 1) {
        ctx.filter = 'contrast(0.95) brightness(1.02)';
    } else if (tier === 2) {
        ctx.filter = 'blur(0.6px) contrast(0.88) brightness(1.05)';
    } else if (tier === 3) {
        ctx.filter = 'blur(1.15px) contrast(0.78) brightness(1.08) saturate(0.85)';
    } else {
        ctx.filter = 'blur(1.7px) contrast(0.68) brightness(1.12) saturate(0.7)';
    }
    ctx.drawImage(source, -source.width / 2, -source.height / 2);
    ctx.restore();
    ctx.filter = 'none';

    if (tier >= 2) {
        const step = tier === 2 ? 16 : tier === 3 ? 8 : 4;
        const amp = tier === 2 ? 28 : tier === 3 ? 42 : 58;
        const img = ctx.getImageData(0, 0, out.width, out.height);
        const data = img.data;
        for (let i = 0; i < data.length; i += step * 4) {
            const n = (rand() - 0.5) * amp;
            data[i] = Math.max(0, Math.min(255, data[i] + n));
            data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + n));
            data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + n));
        }
        ctx.putImageData(img, 0, 0);
    }

    if (tier >= 3) {
        const bands = tier === 3 ? 3 : 6;
        for (let b = 0; b < bands; b++) {
            const y = Math.floor(rand() * out.height);
            const h = 4 + Math.floor(rand() * (tier === 3 ? 10 : 18));
            ctx.fillStyle = `rgba(40, 35, 28, ${tier === 3 ? 0.08 : 0.14})`;
            ctx.fillRect(0, y, out.width, h);
        }
        const g = ctx.createRadialGradient(
            out.width * (0.3 + rand() * 0.4),
            out.height * (0.2 + rand() * 0.3),
            10,
            out.width / 2,
            out.height / 2,
            Math.max(out.width, out.height) * 0.75
        );
        g.addColorStop(0, 'rgba(255,255,255,0)');
        g.addColorStop(0.65, 'rgba(255,255,255,0)');
        g.addColorStop(1, `rgba(30, 24, 16, ${tier === 3 ? 0.18 : 0.32})`);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, out.width, out.height);
    }

    if (tier >= 4) {
        const gx = out.width * (0.15 + rand() * 0.7);
        const gy = out.height * (0.1 + rand() * 0.5);
        const glare = ctx.createRadialGradient(gx, gy, 4, gx, gy, Math.min(out.width, out.height) * 0.28);
        glare.addColorStop(0, 'rgba(255,255,255,0.55)');
        glare.addColorStop(0.35, 'rgba(255,255,255,0.18)');
        glare.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = glare;
        ctx.fillRect(0, 0, out.width, out.height);
        ctx.strokeStyle = `rgba(20, 18, 14, 0.2)`;
        ctx.lineWidth = 2;
        for (let i = 0; i < 4; i++) {
            const x0 = rand() * out.width;
            const y0 = rand() * out.height;
            ctx.beginPath();
            ctx.moveTo(x0, y0);
            ctx.quadraticCurveTo(
                x0 + (rand() - 0.5) * 80,
                y0 + (rand() - 0.5) * 80,
                x0 + (rand() - 0.5) * 160,
                y0 + (rand() - 0.5) * 160
            );
            ctx.stroke();
        }
    }

    return out;
}

export function applyOrientation(
    source: HTMLCanvasElement,
    orientation: FixtureOrientation
): HTMLCanvasElement {
    const turns = ((orientation / 90) | 0) % 4;
    if (!turns) {
        return source;
    }
    const swap = turns % 2 === 1;
    const out = makeCanvas(swap ? source.height : source.width, swap ? source.width : source.height);
    const ctx = out.getContext('2d');
    if (!ctx) {
        return source;
    }
    ctx.translate(out.width / 2, out.height / 2);
    ctx.rotate(turns * Math.PI / 2);
    ctx.drawImage(source, -source.width / 2, -source.height / 2);
    return out;
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(b => {
            if (!b) {
                reject(new Error('canvas.toBlob failed'));
                return;
            }
            resolve(b);
        }, type, quality);
    });
}

export async function encodeRasterBlob(
    canvas: HTMLCanvasElement,
    format: 'png' | 'jpeg',
    quality: FixtureQuality
): Promise<Blob> {
    if (format === 'png') {
        return canvasToBlob(canvas, 'image/png');
    }
    return canvasToBlob(canvas, 'image/jpeg', jpegQualityFor(quality));
}

function finishFixtureCanvas(recipe: FixtureRecipe): HTMLCanvasElement {
    const painted = paintFixtureTemplate(recipe.templateId, recipe.profile);
    const dirtied = applyDirtyEffects(painted.canvas, recipe.quality, recipe.seed);
    return applyOrientation(dirtied, recipe.orientation || 0);
}

export async function renderFixtureRaster(recipe: FixtureRecipe): Promise<{ canvas: HTMLCanvasElement; blob: Blob }> {
    const canvas = finishFixtureCanvas(recipe);
    const format = recipe.format === 'png' ? 'png' : 'jpeg';
    const blob = await encodeRasterBlob(canvas, format, recipe.quality);
    return { canvas, blob };
}

export async function materializeFixtureBlob(recipe: FixtureRecipe): Promise<Blob> {
    const canvas = finishFixtureCanvas(recipe);
    if (recipe.format === 'pdf-image') {
        return buildImageOnlyPdfFromCanvas(canvas, jpegQualityFor(recipe.quality));
    }
    return encodeRasterBlob(canvas, recipe.format === 'png' ? 'png' : 'jpeg', recipe.quality);
}
